import { createRouter, createWebHistory } from "vue-router";
import Shop from "./Shop";
import Auth from "./Auth";
import Info from "./Info";
import ProductInfo from "./ProductInfo";
import Trainings from "./Trainings";
import Sale from "./Sale";
import Point from "./Point";
import Dashboard from "@/components/Dashboard/index";
import ExampleProducts from "@/components/Dashboard/ExampleProducts";
import PageNotFound from "../views/404.vue";
import AccessDenied from "../views/AccessDenied.vue";
import User from "./User";
import MarketingConsents from "./MarketingConsents";
import PortalBonus from "./PortalBonus";
import PortalPromotions from "./PortalPromotions";
import redirectWholesalerFromDashboard from "./redirectWholesalerFromDashboard";
import store from "../store";
import TokenService from "@/services/storage.service";
import { CONSENT_TYPE } from "@/configs/constants";
import {
  abortNavigationToLogin,
  isSessionExpiredResponse,
  isUnauthorizedMeResponse,
} from "@/auth/session";
// import routes by module

// Define a function to dynamically resolve the component based on Vuex state
const resolveComponent = async () => {
  if (store.getters.isLiteVersion) {
    return import(
      /* webpackChunkName: "lite-version" */ "@/layouts/LiteVersion.vue"
    );
  } else {
    return import(/* webpackChunkName: "full-version" */ "@/views/Default.vue");
  }
};

// Portal home (PL/CZ/HU/SK) resolved in DashboardEntry after tenantId is known.
const resolveDashboardComponent = () =>
  import(/* webpackChunkName: "dashboard-entry" */ "@/views/DashboardEntry.vue");

// TODO: remove this import when not needed
import DownloadAttribute from "../components/DownloadAttribute.vue";
const routes = [
  {
    path: "/generate-report",
    name: "generate-report",
    component: resolveComponent,
    props: {
      content: DownloadAttribute,
    },
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: resolveDashboardComponent,
    props: {
      content: Dashboard,
      data: ExampleProducts,
    },
    meta: {
      authRequired: true,
      // TODO: check roles to work with LV. Commented by AZ
      //requiredRole: ["dealer", "admin", "staff"],
    },
  },
  ...PortalBonus,
  ...PortalPromotions,
  ...Shop,
  ...Auth,
  ...Trainings,
  ...ProductInfo,
  ...User,
  ...Info,
  ...Sale,
  ...Point,
  ...MarketingConsents,
  {
    path: "/:pathMatch(.*)*",
    name: "404",
    component: PageNotFound,
  },
  {
    path: "/access-denied",
    name: "accessDenied",
    component: AccessDenied,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    // always scroll to top
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  if (to.name === "login" && TokenService.getToken() && !to.query.token) {
    return next({ name: "dashboard" });
  }

  const hasB2cLoginParams = Boolean(to.query.token && to.query.userId);
  if (
    to.matched.some((record) => record.meta.authRequired) &&
    !TokenService.getToken() &&
    !hasB2cLoginParams
  ) {
    return abortNavigationToLogin(next, to.fullPath);
  }

  // Keep role-based dashboard redirect in one place to avoid duplicating this logic in views.
  if (await redirectWholesalerFromDashboard(to, next, store)) {
    return;
  }

  // Check required role
  let requiredRole = to.meta.requiredRole;
  let requiredPermission = to.meta.permission;
  let requiredModule = to.meta.requiredModule;

  if (requiredRole) {
    try {
      let user = store.getters.getMe || TokenService.getUser();
      let response;
      if (!user) {
        response = await store.dispatch("me");
        user = store.getters.getMe || TokenService.getUser();
      }

      if (isUnauthorizedMeResponse(response)) {
        abortNavigationToLogin(next, to.fullPath);
        return;
      }

      if (!user) {
        return next({ name: "dashboard" });
      }

      if (requiredPermission) {
        let haveAccess = TokenService.showElement(requiredPermission);
        if (!haveAccess) return next({ name: "dashboard" });
      }

      if (requiredModule) {
        const tenantPermissions =
          store.getters.getTenantPermissions ||
          JSON.parse(localStorage.getItem("tenantPermissions") || "[]");
        if (!tenantPermissions.includes(requiredModule)) {
          return next({ name: "dashboard" });
        }
      }

      // case when user needs to be approved as direct/indirect
      //  let restrictedAccess = await store.getters.getAccessRestricted;
      //   if (restrictedAccess === true && to.path !== '/dashboard') {
      //     next("/dashboard")
      //  }

      const hasRole = user.roles?.some((item) => requiredRole.includes(item.name));
      if (!hasRole) {
        return next({ name: "dashboard" });
      }
    } catch (e) {
      console.error("[Router] Role check failed:", e);
      if (isSessionExpiredResponse(e) || !TokenService.getToken()) {
        abortNavigationToLogin(next, to.fullPath);
        return;
      }
      next({ name: "dashboard" });
      return;
    }
  }
  // case when user needs to be approved as direct/indirect
  // Check if the user has restricted access
  // let restrictedAccess = await store.getters.getAccessRestricted;

  // Check if restricted access is true and the route is not "/dashboard"
  // if (restrictedAccess === true && to.path !== '/dashboard') {
  //   next("/dashboard");  // Redirect to dashboard if condition is met
  // } else {
  if (TokenService.getToken() && to.name !== "login" && to.name !== "maintenance") {
    let user = store.getters.getMe || TokenService.getUser();
    if (!user) {
      await store.dispatch("me").catch(() => null);
      user = store.getters.getMe || TokenService.getUser();
    }

    const urlTenantId =
      window.location.href.match(/plus-([a-z]{2})\.bsplus\.bridgestone\.eu/i)?.[1]?.toUpperCase() || "";
    const tenantId = String(store.getters.tenantId || urlTenantId || "").trim().toUpperCase();
    const currentLocale = String(
      store.getters.getCurrentLocale || localStorage.getItem("currentLocale") || ""
    ).trim();
    const isNlTemporaryConsentBypass = tenantId === "NL" || currentLocale === "nl-NL";

    const pendingMarketingConsentTypes = Array.isArray(user?.pending_marketing_consent_types)
      ? user.pending_marketing_consent_types
      : (user?.user_has_required_marketing_consents === true ? [CONSENT_TYPE.ONBOARDING] : []);

    if (!isNlTemporaryConsentBypass && pendingMarketingConsentTypes.includes(CONSENT_TYPE.ONBOARDING)) {
      const allowedWhenPendingConsents = [
        "dashboard",
        "bonusUhrd",
        "bonusAwans",
        "bonusGoals",
        "consents",
        "wholesaler",
      ];
      if (!allowedWhenPendingConsents.includes(to.name)) {
        next({ name: "dashboard" });
        return;
      }
    }
  }

  next();
  // }
});

export default router;
